import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { folder, useControls } from 'leva';
import { useLoader, useThree } from '@react-three/fiber';

interface Props {
  url: string;
  name: string;
  defaultColor: string;
  label: string;
}

export const StlViewer: React.FC<Props> = ({
  url,
  name,
  defaultColor,
  label
}) => {
  const { scene } = useThree();
  const geometry = useLoader(STLLoader, url);

  const {
    color, opacity, roughness, metalness, iridescence, wireframe
  } = useControls(label, {
    [name]: folder({
      color: defaultColor,
      opacity: {
        value: 1,
        min: 0,
        max: 1,
        step: 0.05,
      },
      roughness: {
        value: 1,
        min: 0,
        max: 1,
        step: 0.05,
      },
      metalness: {
        value: 0.5,
        min: 0,
        max: 1,
        step: 0.05,
      },
      iridescence: {
        value: 1,
        min: 0,
        max: 1,
        step: 0.05,
      },
      wireframe: false,
    }, { collapsed: true })
  });

  const material = useMemo(() => new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(color),
    roughness,
    metalness,
    iridescence,
    transparent: true,
    opacity,
    clearcoat: 1.0,
    clearcoatRoughness: 0.25,
    wireframe,
  }), [color, opacity, roughness, metalness, iridescence, wireframe]);

  useEffect(() => {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.scale.set(0.001, 0.001, 0.001);
    scene.add(mesh);
    return () => {
      scene.remove(mesh);
      geometry.dispose();
      material.dispose();
    };
  }, [geometry, material, scene]);

  return null;
};
